'use client';

import { useEffect } from 'react';

import posthog from '@/lib/posthog/client';

import { captureError, getErrorObject } from './captureError';

interface PostHogProviderProps {
  children: React.ReactNode;
}

// don't track in development unless explicitly enabled
const isPosthogDisabled = process.env.NEXT_PUBLIC_NODE_ENV === 'development' && process.env.NEXT_PUBLIC_POSTHOG_DEV_TRACKING !== 'true';

const PostHogProvider = ({ children }: PostHogProviderProps) => {
  useEffect(() => {
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;

    if (!posthogKey) {
      return;
    }

    if (isPosthogDisabled) {
      return;
    }

    const cancelInitialization = posthog.init(posthogKey, {
      api_host: '/ingest',
      ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      defaults: '2025-05-24',
      capture_exceptions: false, // Consent and redaction are applied by captureError.
      disable_session_recording: true,
      disable_surveys: true,
      person_profiles: 'identified_only',
      debug: false,
    });
    const onError = (event: ErrorEvent) => captureError(getErrorObject(event.error ?? event.message), { source: 'WINDOW_ERROR' });
    const onRejection = (event: PromiseRejectionEvent) => captureError(getErrorObject(event.reason), { source: 'UNHANDLED_REJECTION' });
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      cancelInitialization?.();
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return children;
};

export default PostHogProvider;
